import { useEffect, useId, useRef, useState } from 'react';
import { Badge, type BadgeTone } from './Badge';
import { Button } from './Button';

export type SyncItemState =
  'pending' | 'synced' | 'applied' | 'applied_with_exception' | 'rejected' | 'not_delivered';

export interface SyncItem {
  id: string;
  type: string;
  state: SyncItemState;
  /** ISO time the command was made. */
  createdAt: string;
  error?: string | undefined;
}

export interface SyncStatusProps {
  online: boolean;
  /** Epoch ms since the device went offline. */
  offlineSince?: number | null;
  /** Commands not yet confirmed by the server. */
  waiting: number;
  rejected: number;
  notDelivered?: number;
  /** Photos and signatures on this phone not yet uploaded. */
  uploads?: number;
  lastSyncedAt?: number | null;
  items?: readonly SyncItem[];
  onResend?: (id: string) => void;
  onDismiss?: (id: string) => void;
}

const STATE_LABEL: Record<SyncItemState, string> = {
  pending: 'Waiting to sync',
  synced: 'Sent, awaiting result',
  applied: 'Done',
  applied_with_exception: 'Done, needs review',
  rejected: 'Rejected',
  not_delivered: 'Not delivered',
};

const STATE_TONE: Record<SyncItemState, BadgeTone> = {
  pending: 'warning',
  synced: 'info',
  applied: 'success',
  applied_with_exception: 'warning',
  rejected: 'danger',
  not_delivered: 'danger',
};

export function syncStateLabel(state: SyncItemState): string {
  return STATE_LABEL[state];
}

export function syncStateTone(state: SyncItemState): BadgeTone {
  return STATE_TONE[state];
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** The one-line summary: "All synced", "3 waiting", "Offline since 10:42". */
export function syncSummary(
  p: Pick<SyncStatusProps, 'online' | 'offlineSince' | 'waiting' | 'rejected' | 'uploads'>,
): {
  label: string;
  tone: 'ok' | 'waiting' | 'offline' | 'problem';
} {
  const photos = p.uploads ?? 0;
  const photoText = `${photos} photo${photos === 1 ? '' : 's'}`;
  if (p.rejected > 0) return { label: `${p.rejected} rejected`, tone: 'problem' };
  if (!p.online) {
    const since = p.offlineSince ? ` since ${clock(p.offlineSince)}` : '';
    const waiting = [p.waiting > 0 ? `${p.waiting} waiting` : '', photos > 0 ? photoText : '']
      .filter(Boolean)
      .join(', ');
    return {
      label: waiting !== '' ? `Offline, ${waiting}` : `Offline${since}`,
      tone: 'offline',
    };
  }
  if (p.waiting > 0 || photos > 0) {
    const label = [
      p.waiting > 0 ? `${p.waiting} waiting` : '',
      photos > 0 ? `${photoText} to upload` : '',
    ]
      .filter(Boolean)
      .join(', ');
    return { label, tone: 'waiting' };
  }
  return { label: 'All synced', tone: 'ok' };
}

export function SyncStatus(props: SyncStatusProps) {
  const { items = [], onResend, onDismiss, lastSyncedAt } = props;
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const summary = syncSummary(props);
  // Newest first; finished commands only matter for the last few.
  const visible = [...items]
    .reverse()
    .filter((i, idx) => i.state !== 'applied' || idx < 5)
    .slice(0, 30);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="eb-sync" data-tone={summary.tone} ref={root}>
      <button
        type="button"
        className="eb-sync__btn"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="eb-sync__dot" aria-hidden="true" />
        <span role="status">{summary.label}</span>
      </button>
      {open ? (
        <div className="eb-sync__panel" id={panelId} role="region" aria-label="Sync details">
          <h2 className="eb-sync__title">
            {props.online ? 'Online' : 'Offline'}
            {lastSyncedAt ? ` · last synced ${clock(lastSyncedAt)}` : ''}
          </h2>
          {visible.length === 0 ? (
            <p className="eb-sync__muted">
              Nothing is waiting. Everything you did has reached the server.
            </p>
          ) : (
            <ul className="eb-sync__list">
              {visible.map((item) => (
                <li key={item.id} className="eb-sync__item">
                  <div className="eb-sync__row">
                    <strong>{item.type}</strong>
                    <Badge tone={STATE_TONE[item.state]}>{STATE_LABEL[item.state]}</Badge>
                  </div>
                  <div className="eb-sync__muted">{new Date(item.createdAt).toLocaleString()}</div>
                  {item.error ? <div className="eb-sync__err">{item.error}</div> : null}
                  <div className="eb-sync__row">
                    {item.state === 'not_delivered' && onResend ? (
                      <Button size="sm" variant="secondary" onClick={() => onResend(item.id)}>
                        Resend
                      </Button>
                    ) : null}
                    {item.state === 'rejected' && onDismiss ? (
                      <Button size="sm" variant="ghost" onClick={() => onDismiss(item.id)}>
                        Dismiss
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
